import { useParams } from "react-router-dom";
import { OverviewDetails } from "./_components/OverviewDetails";
import { useQuery } from "@apollo/client";
import { PERSON_OVERVIEW_QUERY, INSIGHTS_QUERY } from "./queries";
import { Person } from "@/types";
import { MetricGridSkeleton, TableSkeleton } from "@/components/Skeleton";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useRecentlyVisited } from "@/hooks/useRecentlyVisited";
import { useCanViewInteractionsBool, useHasCapabilityBool, DOMAIN, PERMISSION } from "@/hooks/useCapabilities";
import { useEffect, useMemo } from "react";
import { renderName } from "@/lib/personHelpers";
import { useNatlCommitteeManagementEnabled } from "../../membership-services/committees/useNatlCommitteeManagementEnabled";

export default function PersonOverview() {
  const { pgaId } = useParams();
  const canViewInteractions = useCanViewInteractionsBool();
  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const canViewSection = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.viewSection
  });
  const useCommitteeHistory = useNatlCommitteeManagementEnabled();
  const showPersonHistoryPreview = canViewInteractions;
  const { addRecentlyVisitedItem } = useRecentlyVisited();

  const { data, error } = useQuery(PERSON_OVERVIEW_QUERY, {
    variables: {
      pgaId: pgaId!,
      includeProgramMetrics: true,
      includeMembershipApplications: canManageApplications,
      // UniversityCohortType requires viewSection; omit until the user can authorize it.
      includeUniversityCohort: canViewSection,
      useCommitteeHistory
    }
  });

  const person = useMemo<Person | undefined>(() => {
    if (!data?.person) return undefined;
    const { committeeHistory, legacyCommitteeHistory, ...rest } = data.person;
    return {
      ...rest,
      committeeHistory: useCommitteeHistory ? committeeHistory : legacyCommitteeHistory
    };
  }, [data?.person, useCommitteeHistory]);

  useEffect(() => {
    if (person) {
      addRecentlyVisitedItem({
        url: window.location.pathname,
        text: renderName(
          person.displayName,
          person.informalName,
          person.preferredLastName,
          person.suffixName,
          person.middleName,
          person.firstName
        ),
        subtext: person.primarySection?.name || "",
        image: person.profilePhoto || "",
        type: "people"
      });
    }
  }, [person, addRecentlyVisitedItem]);

  const { data: insightsData } = useQuery(INSIGHTS_QUERY, {
    variables: { pgaId: person?.pgaId ?? "" },
    skip: !person?.pgaId || !person?.latestProgram?.programType,
    // Show any cached note immediately, then revalidate so revisits can pick up a newer backend note.
    fetchPolicy: "cache-and-network"
  });

  if (error) {
    return <ErrorBoundary variant="inline" title="Failed to Load Overview" message={error.message} className="mt-12" />;
  }

  if (!person) {
    return (
      <div data-testid="overview-skeleton">
        <MetricGridSkeleton cards={4} layout="expanded" />
        {showPersonHistoryPreview && <TableSkeleton columns={5} rows={4} />}
      </div>
    );
  }

  return <OverviewDetails person={person} insights={insightsData?.insights} />;
}
